// src/ApplicationForm.jsx

import React, { useState, useEffect, useRef } from "react";

const ApplicationForm = ({ isOpen, onClose, role, openRoles }) => {
    const [selectedRole, setSelectedRole] = useState(role);
    const formRef = useRef(null); // Ref to the form content

    useEffect(() => {
        setSelectedRole(role);
    }, [role]);

    if (!isOpen) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const data = Object.fromEntries(formData.entries());
        console.log("Form data submitted:", data);
        console.log("Form submitted for role:", selectedRole);
        onClose();
    };

    // New function to handle clicks outside the form content
    const handleOverlayClick = (e) => {
        // If the click is on the overlay itself and not within the formRef content
        if (formRef.current && !formRef.current.contains(e.target)) {
            onClose();
        }
    };

    return (
        <div 
            className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4"
            onClick={handleOverlayClick} // Add the click handler here
        >
            <div 
                ref={formRef} // Attach the ref to the form content div
                className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6 sm:p-8 transform transition-all duration-300 scale-100 opacity-100"
            >
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>

                {/* Form Header */}
                <div className="text-center mb-6">
                    <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                        Apply for a Role
                    </h2>
                    <p className="text-sm text-gray-500">
                        Please fill out the form below to submit your application.
                    </p>
                </div>

                {/* The Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Role Selection Dropdown */}
                    <div>
                        <label
                            htmlFor="role-select"
                            className="block text-sm font-medium text-gray-700"
                        >
                            Select Role
                        </label>
                        <select
                            id="role-select"
                            name="role"
                            value={selectedRole || ''}
                            onChange={(e) => setSelectedRole(e.target.value)}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            required
                        >
                            <option value="" disabled>Select a position</option>
                            {openRoles.map((job) => (
                                <option key={job.title} value={job.title}>
                                    {job.title}
                                </option>
                            ))}
                            <option value="General Application">General Application</option>
                        </select>
                    </div>

                    {/* Full Name Input */}
                    <div>
                        <label
                            htmlFor="name"
                            className="block text-sm font-medium text-gray-700"
                        >
                            Full Name
                        </label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            required
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                    </div>

                    {/* Email Input */}
                    <div>
                        <label
                            htmlFor="email"
                            className="block text-sm font-medium text-gray-700"
                        >
                            Email Address
                        </label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            required
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                    </div>

                    {/* Phone Number Input */}
                    <div>
                        <label
                            htmlFor="phone"
                            className="block text-sm font-medium text-gray-700"
                        >
                            Phone Number
                        </label>
                        <input
                            type="tel"
                            id="phone"
                            name="phone"
                            required
                            pattern="[0-9]{10}"
                            title="Phone number must be 10 digits"
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                    </div>

                    {/* Resume Upload */}
                    <div>
                        <label
                            htmlFor="resume"
                            className="block text-sm font-medium text-gray-700"
                        >
                            Upload Resume
                        </label>
                        <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                            <div className="space-y-1 text-center">
                                <svg
                                    className="mx-auto h-12 w-12 text-gray-400"
                                    stroke="currentColor"
                                    fill="none"
                                    viewBox="0 0 48 48"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-20m32-12a4 4 0 00-4-4h-20a4 4 0 00-4 4v20a4 4 0 004 4h20a4 4 0 004-4v-20z"
                                        strokeWidth={2}
                                    />
                                </svg>
                                <div className="flex text-sm text-gray-600">
                                    <label
                                        htmlFor="file-upload"
                                        className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                                    >
                                        <span>Upload a file</span>
                                        <input
                                            id="file-upload"
                                            name="file-upload"
                                            type="file"
                                            className="sr-only"
                                            accept=".pdf, .doc, .docx"
                                            required
                                        />
                                    </label>
                                    <p className="pl-1">or drag and drop</p>
                                </div>
                                <p className="text-xs text-gray-500">PDF, DOC, DOCX up to 10MB</p>
                            </div>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="flex justify-end pt-4">
                        <button
                            type="submit"
                            className="px-6 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-medium rounded-lg shadow-md hover:shadow-lg hover:scale-105 transition-transform duration-300"
                        >
                            Submit Application
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ApplicationForm;